import { useState } from 'react';
import { XMarkIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';

export default function SystemSettingsForm({ onSave, onCancel }) {
  const [formData, setFormData] = useState({
    file: null,
    type: 'filament',
    name: '',
    metadata: {},
    forceUpload: false
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [metadataFields, setMetadataFields] = useState([]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Validate file
    if (!formData.file) {
      setError('Please select a JSON file to upload');
      setLoading(false);
      return;
    }

    // Validate file type
    if (!formData.file.name.endsWith('.json')) {
      setError('Only JSON files are allowed');
      setLoading(false);
      return;
    }

    // Validate file size (max 10MB)
    if (formData.file.size > 10 * 1024 * 1024) {
      setError('File size must not exceed 10MB');
      setLoading(false);
      return;
    }

    try {
      // Build metadata object from fields
      const metadata = {};
      metadataFields.forEach(field => {
        if (field.key && field.value) {
          metadata[field.key] = field.value;
        }
      });

      await onSave({
        ...formData,
        metadata
      });
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to upload system setting');
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData({
        ...formData,
        file,
        // Auto-set name from filename (without extension) if name is empty
        name: formData.name || file.name.replace('.json', '')
      });
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const addMetadataField = () => {
    setMetadataFields([...metadataFields, { key: '', value: '' }]);
  };

  const removeMetadataField = (index) => {
    setMetadataFields(metadataFields.filter((_, i) => i !== index));
  };

  const updateMetadataField = (index, field, value) => {
    const newFields = [...metadataFields];
    newFields[index][field] = value;
    setMetadataFields(newFields);
  };

  return (
    <div className="fixed inset-0 bg-slate-900 bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-slate-50">
          <h2 className="text-2xl font-bold text-slate-900">Upload System Setting</h2>
          <button
            onClick={onCancel}
            className="text-slate-400 hover:text-slate-600 transition-colors"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
          <div className="px-6 py-6 space-y-6">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-sm">
                {error}
              </div>
            )}

            {/* File Upload */}
            <div>
              <label htmlFor="file" className="block text-sm font-medium text-slate-700 mb-2">
                JSON File *
              </label>
              <input
                type="file"
                id="file"
                accept=".json"
                onChange={handleFileChange}
                required
                disabled={loading}
                className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-slate-100 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
              />
              <p className="mt-1 text-xs text-slate-500">
                Upload a JSON configuration file (max 10MB)
              </p>
            </div>

            {/* Type Selection */}
            <div>
              <label htmlFor="type" className="block text-sm font-medium text-slate-700 mb-2">
                Type *
              </label>
              <select
                id="type"
                name="type"
                value={formData.type}
                onChange={handleChange}
                required
                disabled={loading}
                className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-slate-100 bg-white"
              >
                <option value="filament">Filament</option>
                <option value="machine">Machine</option>
                <option value="process">Process</option>
              </select>
              <p className="mt-1 text-xs text-slate-500">
                Select the type of slicer setting
              </p>
            </div>

            {/* Name */}
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-slate-700 mb-2">
                Name (Optional)
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                disabled={loading}
                maxLength={255}
                className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-slate-100"
                placeholder="Leave empty to use filename"
              />
              <p className="mt-1 text-xs text-slate-500">
                Custom name for the setting. If not provided, the filename (without extension) will be used
              </p>
            </div>

            {/* Metadata */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <label className="block text-sm font-medium text-slate-700">
                  Metadata (Optional)
                </label>
                <button
                  type="button"
                  onClick={addMetadataField}
                  disabled={loading}
                  className="inline-flex items-center px-3 py-1.5 text-xs bg-green-100 hover:bg-green-200 text-green-700 rounded-md transition-colors font-medium"
                >
                  <PlusIcon className="h-3 w-3 mr-1" />
                  Add Field
                </button>
              </div>

              {metadataFields.length === 0 ? (
                <p className="text-xs text-slate-500">
                  No metadata fields added. Click "Add Field" to add key-value pairs.
                </p>
              ) : (
                <div className="space-y-2">
                  {metadataFields.map((field, index) => (
                    <div key={index} className="bg-slate-50 rounded-lg p-3 border border-slate-200">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        <input
                          type="text"
                          placeholder="Key"
                          value={field.key}
                          onChange={(e) => updateMetadataField(index, 'key', e.target.value)}
                          disabled={loading}
                          className="px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-slate-100 text-sm"
                        />
                        <input
                          type="text"
                          placeholder="Value"
                          value={field.value}
                          onChange={(e) => updateMetadataField(index, 'value', e.target.value)}
                          disabled={loading}
                          className="px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-slate-100 text-sm"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => removeMetadataField(index)}
                        disabled={loading}
                        className="mt-2 inline-flex items-center px-2 py-1 text-xs bg-red-100 hover:bg-red-200 text-red-700 rounded-md transition-colors"
                      >
                        <TrashIcon className="h-3 w-3 mr-1" />
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <p className="text-xs text-slate-500">
                Add additional information as key-value pairs (e.g., manufacturer, tested, etc.)
              </p>
            </div>

            {/* Force Upload */}
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="forceUpload"
                name="forceUpload"
                checked={formData.forceUpload}
                onChange={handleChange}
                disabled={loading}
                className="w-4 h-4 text-blue-600 border-slate-300 rounded focus:ring-blue-500"
              />
              <label htmlFor="forceUpload" className="text-sm font-medium text-slate-700">
                Force Upload (Overwrite Existing)
              </label>
            </div>
            <p className="text-xs text-slate-500 -mt-2 ml-6">
              If enabled, this will overwrite any existing setting with the same type and name
            </p>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-200 bg-slate-50">
            <button
              type="button"
              onClick={onCancel}
              disabled={loading}
              className="px-5 py-2.5 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-5 py-2.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-50 transition-colors shadow-sm"
            >
              {loading ? 'Uploading...' : 'Upload Setting'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
